import React from "react";
import { alpha, Box, IconButton, Paper, Typography, useTheme } from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import CloseIcon from "@mui/icons-material/Close";
import { NewsItem } from "../../shared/interfaces/NewsItem";
import { formatNewsTimestamp } from "../../shared/utils/newsFilters";

interface MaintenanceBannerProps {
  items: NewsItem[];
  onDismiss: (newsId: string) => void;
}

export const MaintenanceBanner: React.FC<MaintenanceBannerProps> = ({
  items,
  onDismiss,
}) => {
  const theme = useTheme();

  if (items.length === 0) {
    return null;
  }

  return (
    <Box sx={{ px: 3, mb: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
      {items.map((item) => (
        <Paper
          key={item.newsId}
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: alpha("#8b5cf6", 0.08),
            border: `1px solid ${alpha("#8b5cf6", 0.2)}`,
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: alpha("#8b5cf6", 0.15),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <BuildIcon sx={{ fontSize: 20, color: "#8b5cf6" }} />
          </Box>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                lineHeight: 1.4,
                mb: 0.25,
              }}
            >
              {item.title}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                color: "text.secondary",
                fontSize: "0.7rem",
                mb: 0.75,
              }}
            >
              {formatNewsTimestamp(item.createdAt)}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                lineHeight: 1.5,
                fontSize: "0.85rem",
                "& b, & strong": {
                  color: "text.primary",
                  fontWeight: 600,
                },
                "& a": {
                  color: "#8b5cf6",
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                },
              }}
              dangerouslySetInnerHTML={{ __html: item.content }}
            />
          </Box>

          <IconButton
            onClick={() => onDismiss(item.newsId)}
            size="small"
            sx={{
              color: "text.secondary",
              flexShrink: 0,
              mt: -0.5,
              mr: -0.5,
              "&:hover": {
                backgroundColor: alpha(theme.palette.text.primary, 0.08),
              },
            }}
            aria-label="Dismiss maintenance notice"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>
      ))}
    </Box>
  );
};
